import * as React from 'react';
import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { isNil, path, pathOr } from 'ramda';
import { QuestionLayout } from '../../components/QuestionLayout';
import { isEmptyString, isValidNumber } from '../../utils/validators';
import { PriceRangeQuestion } from '../../components/PriceQuestion';
import { formatPrice, getNumberString, isValidPrice, usePriceOptions, validateGte, validateLte } from './utils';
import { PriceEstimationContainer } from '../PriceEstimationContainer';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';

export const PriceRangeQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, unit, title, required },
    setAnswersState,
    setNextStep,
    answersState,
  } = props;

  const { trackSubmit, trackChange, trackLteWithoutGte } = useFormTracking();
  const { selectedOptions, options, handleSelect } = usePriceOptions(props);

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm<string>({
    onSubmit(values) {
      setAnswersState(prevState => {
        const gte = isValidPrice(values['gte']) ? Number(getNumberString(values['gte'])) : null;

        const lte = isValidPrice(values['lte']) ? Number(getNumberString(values['lte'])) : null;

        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${gte}-${lte}`,
        });

        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: {
              gte,
              lte,
              options: selectedOptions,
            },
          },
        };

        setNextStep(nextAnswersState);

        return nextAnswersState;
      });
    },
    validators: {
      gte: (value, values) => validateGte(value, path(['lte'], values) as string, required),
      lte: (value, values) => validateLte(path(['gte'], values) as string, value, required),
    },
    initialValues: pickOnlyMeaning({
      gte: formatPrice(pathOr('', [answerField, 'value', 'gte'], answersState)),
      lte: formatPrice(pathOr('', [answerField, 'value', 'lte'], answersState)),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      const value = e.target.value;

      if (e.target.name === 'lte' && !values['gte']) {
        trackLteWithoutGte({
          isRequired: required,
          field: answerField,
          value,
        });
      }

      if (isNil(value) || isEmptyString(value)) {
        return onChange(e.target.name, value);
      }

      const numberValue = getNumberString(value);
      if (isValidNumber(numberValue)) {
        return onChange(e.target.name, formatPrice(numberValue));
      }
    },
    [onChange, trackLteWithoutGte, trackChange, answerField, required, values],
  );

  return (
    <QuestionLayout
      title={title}
      onSubmit={handleSubmit}
      footer={<PriceEstimationContainer />}
      disableSubmit={isSubmitDisabled}
    >
      <PriceRangeQuestion
        unit={unit}
        gteValue={pathOr('', ['gte'], values)}
        gteError={errors['gte']}
        gtePlaceholder={'от'}
        lteValue={pathOr('', ['lte'], values)}
        lteError={errors['lte']}
        ltePlaceholder={'до'}
        handleChange={handleChange}
        onSelect={handleSelect}
        selectedOptions={selectedOptions}
        options={options}
        lteInputName={`${answerField}_lte`}
        gteInputName={`${answerField}_gte`}
      />
    </QuestionLayout>
  );
};

PriceRangeQuestionContainer.displayName = 'RangeQuestionContainer';
